/**
 * useMapSensorData Hook
 *
 * Real-time sensor data hook for the MapComponent.
 * Combines query template data fetching with WebSocket subscriptions
 * for live sensor updates.
 *
 * Phase 4: Real-Time Data Updates Implementation
 * Created: January 2026
 *
 * Features:
 * - Query template data binding
 * - WebSocket subscription for real-time updates
 * - Automatic reconnection with exponential backoff
 * - Recently updated sensor tracking
 * - Sensor state management (online/offline/stale)
 * - Configurable refresh intervals
 */

import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { ChartDataSource } from "@/types/chartDataSource";
import { socketClient } from "@/services/websocket/socketClient";
import {
  useQueryTemplateData,
  isQueryTemplateDataSource,
} from "@/hooks/useQueryTemplateData";
import {
  registerRefetch,
  unregisterRefetch,
  getRefetch,
  useRefreshStore,
} from "@/stores/data/refresh-store";
import type {
  SensorDataPoint,
  SensorState,
  RealTimeConfig,
  MapPin,
} from "@/components/charts/MapComponent.types";
import { OperationType } from "@/types";

// ============================================================================
// Types
// ============================================================================

export interface UseMapSensorDataOptions {
  /** Data source configuration from component properties */
  dataSource?: ChartDataSource;
  /** Component ID for logging/caching */
  componentId: string;
  /** Whether to execute the query (default: true) */
  enabled?: boolean;
  /** Enable real-time WebSocket updates */
  enableRealTime?: boolean;
  /** Auto-refresh interval in seconds (0 = disabled) */
  refreshInterval?: number;
  /** WebSocket channel group ID for subscription */
  groupId?: string;
  /** Seconds before marking sensor as stale */
  staleThreshold?: number;
  /** Seconds before marking sensor as offline */
  offlineThreshold?: number;
  /** Pins array — used to detect pin data bindings for refresh badge */
  pins?: MapPin[];
}

export interface UseMapSensorDataResult {
  /** Fetched sensor data array */
  data: SensorDataPoint[] | null;
  /** Loading state */
  isLoading: boolean;
  /** Error message if fetch failed */
  error: string | null;
  /** Manually trigger a data refresh */
  refetch: () => Promise<void>;
  /** Timestamp of last successful update */
  lastUpdated: Date | null;
  /** WebSocket connection status */
  connectionStatus: "connected" | "disconnected" | "reconnecting";
  /** Set of sensor IDs updated in last 5 seconds */
  recentlyUpdated: Set<string>;
  /** Sensor state map (online/offline/stale) */
  sensorStates: Map<string, SensorState>;
  /** Real-time mode status */
  isRealTimeEnabled: boolean;
  /** Toggle real-time mode */
  toggleRealTime: (enabled: boolean) => void;
  /** Update refresh interval */
  setRefreshInterval: (seconds: number) => void;
}

// ============================================================================
// Constants
// ============================================================================

const RECENTLY_UPDATED_DURATION = 5000; // 5 seconds
const DEFAULT_STALE_THRESHOLD = 300; // 5 minutes
const DEFAULT_OFFLINE_THRESHOLD = 900; // 15 minutes
const DEBOUNCE_DELAY = 100; // 100ms debounce for rapid updates

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

const parseSensorDataPoint = (value: unknown): SensorDataPoint | null => {
  if (!isRecord(value)) {
    return null;
  }

  const {
    id,
    deviceId,
    latitude,
    longitude,
    moistureLevel,
    temperature,
    depth,
    zone,
    timestamp,
    batteryLevel,
  } = value;

  if (
    typeof id !== "string" ||
    typeof deviceId !== "string" ||
    !isFiniteNumber(latitude) ||
    !isFiniteNumber(longitude) ||
    !isFiniteNumber(moistureLevel) ||
    !(typeof timestamp === "string" || timestamp instanceof Date)
  ) {
    return null;
  }

  const sensor: SensorDataPoint = {
    id,
    deviceId,
    latitude,
    longitude,
    moistureLevel,
    timestamp,
  };

  if (isFiniteNumber(temperature)) {
    sensor.temperature = temperature;
  }

  if (isFiniteNumber(depth)) {
    sensor.depth = depth;
  }

  if (typeof zone === "string") {
    sensor.zone = zone;
  }

  if (isFiniteNumber(batteryLevel)) {
    sensor.batteryLevel = batteryLevel;
  }

  return sensor;
};

const parseSensorDataPoints = (value: unknown): SensorDataPoint[] | null => {
  if (!Array.isArray(value)) {
    return null;
  }

  const sensors: SensorDataPoint[] = [];
  for (const item of value) {
    const sensor = parseSensorDataPoint(item);
    if (!sensor) {
      return null;
    }
    sensors.push(sensor);
  }

  return sensors;
};

interface SensorUpdatePayload {
  sensorId: string;
  moisture: number;
  temperature?: number;
  timestamp: string;
  latitude?: number;
  longitude?: number;
  zone?: string;
}

const parseSensorUpdatePayload = (
  value: unknown,
): SensorUpdatePayload | null => {
  if (!isRecord(value)) {
    return null;
  }

  const {
    sensorId,
    moisture,
    temperature,
    timestamp,
    latitude,
    longitude,
    zone,
  } = value;

  if (
    typeof sensorId !== "string" ||
    !isFiniteNumber(moisture) ||
    typeof timestamp !== "string"
  ) {
    return null;
  }

  const payload: SensorUpdatePayload = {
    sensorId,
    moisture,
    timestamp,
  };

  if (isFiniteNumber(temperature)) {
    payload.temperature = temperature;
  }

  if (isFiniteNumber(latitude)) {
    payload.latitude = latitude;
  }

  if (isFiniteNumber(longitude)) {
    payload.longitude = longitude;
  }

  if (typeof zone === "string") {
    payload.zone = zone;
  }

  return payload;
};

// ============================================================================
// Hook Implementation
// ============================================================================

/**
 * Hook to fetch and manage real-time sensor data for the SoilMoistureMap
 *
 * @example
 * ```tsx
 * const {
 *   data,
 *   isLoading,
 *   error,
 *   refetch,
 *   connectionStatus,
 *   recentlyUpdated,
 * } = useMapSensorData({
 *   dataSource: component.properties.dataSource,
 *   componentId: component.id,
 *   enableRealTime: true,
 *   groupId: 'field-sensors',
 * });
 * ```
 */
export function useMapSensorData({
  dataSource,
  componentId,
  enabled = true,
  enableRealTime: initialEnableRealTime = false,
  refreshInterval: initialRefreshInterval = 0,
  groupId,
  staleThreshold = DEFAULT_STALE_THRESHOLD,
  offlineThreshold = DEFAULT_OFFLINE_THRESHOLD,
  pins,
}: UseMapSensorDataOptions): UseMapSensorDataResult {
  // Detect pin data bindings — pins with dataBindings are data-aware even
  // without a component-level query template.
  const hasPinData = useMemo(
    () => (pins ?? []).some((p) => (p.dataBindings?.length ?? 0) > 0),
    [pins],
  );

  // ---------------------------------------------------------------------------
  // State Management
  // ---------------------------------------------------------------------------
  const [sensorData, setSensorData] = useState<SensorDataPoint[] | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<
    "connected" | "disconnected" | "reconnecting"
  >("disconnected");
  const [recentlyUpdatedIds, setRecentlyUpdatedIds] = useState<Set<string>>(
    new Set(),
  );
  const [sensorStates, setSensorStates] = useState<Map<string, SensorState>>(
    new Map(),
  );
  const [isRealTimeEnabled, setIsRealTimeEnabled] = useState(
    initialEnableRealTime,
  );
  const [refreshIntervalSeconds, setRefreshIntervalSeconds] = useState(
    initialRefreshInterval,
  );

  // Refs for cleanup and debouncing
  const mountedRef = useRef(true);
  const updateTimeoutsRef = useRef<Map<string, NodeJS.Timeout>>(new Map());
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const pendingUpdatesRef = useRef<Map<string, SensorDataPoint>>(new Map());
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const stateCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // ---------------------------------------------------------------------------
  // Query Template Data Fetching
  // ---------------------------------------------------------------------------
  const hasQueryTemplate = isQueryTemplateDataSource(dataSource);

  const {
    data: queryTemplateData,
    isLoading: qtLoading,
    error: qtError,
    refetch: qtRefetch,
    lastFetched,
  } = useQueryTemplateData({
    dataSource,
    componentId,
    enabled: enabled && hasQueryTemplate,
  });

  // ---------------------------------------------------------------------------
  // Initialize sensor data from query template
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const sensors = parseSensorDataPoints(queryTemplateData);
    if (sensors) {
      setSensorData(sensors);
      setLastUpdated(lastFetched);

      // Initialize sensor states
      const now = new Date();
      const newStates = new Map<string, SensorState>();
      sensors.forEach((sensor) => {
        newStates.set(sensor.id, {
          id: sensor.id,
          status: "online",
          lastSeen: now,
          lastValue: sensor.moistureLevel,
        });
      });
      setSensorStates(newStates);
    }
  }, [queryTemplateData, lastFetched]);

  // ---------------------------------------------------------------------------
  // WebSocket Connection Status Monitoring
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!isRealTimeEnabled) {
      setConnectionStatus("disconnected");
      return;
    }

    const checkConnection = () => {
      const isConnected = socketClient.isConnected();
      const state = socketClient.getConnectionState();

      if (isConnected) {
        setConnectionStatus("connected");
      } else if (state === "reconnecting") {
        setConnectionStatus("reconnecting");
      } else {
        setConnectionStatus("disconnected");
      }
    };

    // Initial check
    checkConnection();

    // Subscribe to connection events
    const unsubscribers = [
      socketClient.on("connected", () => {
        setConnectionStatus("connected");
      }),

      socketClient.on("disconnected", () => {
        setConnectionStatus("disconnected");
      }),

      socketClient.on("reconnecting", () => {
        setConnectionStatus("reconnecting");
      }),

      socketClient.on("reconnected", () => {
        setConnectionStatus("connected");
      }),
    ];

    // Check connection periodically
    const intervalId = setInterval(checkConnection, 5000);

    return () => {
      clearInterval(intervalId);
      unsubscribers.forEach((unsubscribe) => unsubscribe());
    };
  }, [isRealTimeEnabled]);

  // ---------------------------------------------------------------------------
  // Process Pending Updates (Debounced)
  // ---------------------------------------------------------------------------
  const processPendingUpdates = useCallback(() => {
    const updates = pendingUpdatesRef.current;
    if (updates.size === 0) return;

    const updatedIds = new Set<string>();

    setSensorData((prevData) => {
      if (!prevData) return prevData;

      const newData = [...prevData];
      const now = new Date();

      updates.forEach((update, sensorId) => {
        const existingIndex = newData.findIndex((s) => s.id === sensorId);

        if (existingIndex >= 0) {
          // Update existing sensor
          newData[existingIndex] = {
            ...newData[existingIndex],
            moistureLevel: update.moistureLevel,
            temperature: update.temperature,
            timestamp: update.timestamp,
          };
        } else if (update.latitude && update.longitude) {
          // Add new sensor
          newData.push(update);
        }

        updatedIds.add(sensorId);

        // Update sensor state
        setSensorStates((prev) => {
          const newStates = new Map(prev);
          newStates.set(sensorId, {
            id: sensorId,
            status: "online",
            lastSeen: now,
            lastValue: update.moistureLevel,
          });
          return newStates;
        });
      });

      return newData;
    });

    // Track recently updated sensors
    setRecentlyUpdatedIds((prev) => new Set([...prev, ...updatedIds]));

    // Clear recently updated after 5 seconds
    updatedIds.forEach((sensorId) => {
      // Clear any existing timeout for this sensor
      const existingTimeout = updateTimeoutsRef.current.get(sensorId);
      if (existingTimeout) {
        clearTimeout(existingTimeout);
      }

      // Set new timeout
      const timeoutId = setTimeout(() => {
        setRecentlyUpdatedIds((prev) => {
          const next = new Set(prev);
          next.delete(sensorId);
          return next;
        });
        updateTimeoutsRef.current.delete(sensorId);
      }, RECENTLY_UPDATED_DURATION);

      updateTimeoutsRef.current.set(sensorId, timeoutId);
    });

    // Update last updated timestamp
    setLastUpdated(new Date());

    // Clear pending updates
    pendingUpdatesRef.current.clear();
  }, []);

  // ---------------------------------------------------------------------------
  // WebSocket Subscription for Sensor Updates
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!isRealTimeEnabled || !enabled) {
      return;
    }

    // Handler for incoming sensor updates
    const handleSensorUpdate = (data: {
      sensorId: string;
      moisture: number;
      temperature?: number;
      timestamp: string;
      latitude?: number;
      longitude?: number;
      zone?: string;
    }) => {
      if (!mountedRef.current) return;

      // Add to pending updates (debounced)
      pendingUpdatesRef.current.set(data.sensorId, {
        id: data.sensorId,
        deviceId: data.sensorId,
        moistureLevel: data.moisture,
        temperature: data.temperature,
        latitude: data.latitude || 0,
        longitude: data.longitude || 0,
        zone: data.zone,
        timestamp: data.timestamp,
      });

      // Debounce updates
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        if (!mountedRef.current) return;
        processPendingUpdates();
      }, DEBOUNCE_DELAY);
    };

    // Subscribe to sensor update events
    // This would be emitted by the server when sensor data changes
    const unsubscribeOperationReceived = socketClient.on(
      "operationReceived",
      (message) => {
        // Check if this is a sensor update operation
        if (message.payload?.operation?.type === OperationType.SENSOR_UPDATE) {
          const updateData = parseSensorUpdatePayload(
            message.payload.operation.data,
          );
          if (updateData) {
            handleSensorUpdate(updateData);
          }
        }
      },
    );

    return () => {
      unsubscribeOperationReceived();

      // Clear debounce timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [isRealTimeEnabled, enabled, groupId, processPendingUpdates]);

  // ---------------------------------------------------------------------------
  // Sensor State Monitoring (Stale/Offline Detection)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!enabled) return;

    const checkSensorStates = () => {
      const now = Date.now();

      setSensorStates((prev) => {
        const newStates = new Map(prev);
        let hasChanges = false;

        newStates.forEach((state, sensorId) => {
          const timeSinceLastSeen = (now - state.lastSeen.getTime()) / 1000;

          let newStatus: "online" | "offline" | "stale" = "online";
          if (timeSinceLastSeen >= offlineThreshold) {
            newStatus = "offline";
          } else if (timeSinceLastSeen >= staleThreshold) {
            newStatus = "stale";
          }

          if (state.status !== newStatus) {
            newStates.set(sensorId, { ...state, status: newStatus });
            hasChanges = true;
          }
        });

        return hasChanges ? newStates : prev;
      });
    };

    // Check every 30 seconds
    stateCheckIntervalRef.current = setInterval(checkSensorStates, 30000);

    return () => {
      if (stateCheckIntervalRef.current) {
        clearInterval(stateCheckIntervalRef.current);
      }
    };
  }, [enabled, staleThreshold, offlineThreshold]);

  // ---------------------------------------------------------------------------
  // Auto-Refresh Interval
  // ---------------------------------------------------------------------------
  useEffect(() => {
    // Clear any existing interval
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
    }

    if (!enabled || refreshIntervalSeconds <= 0) return;

    refreshIntervalRef.current = setInterval(() => {
      qtRefetch();
    }, refreshIntervalSeconds * 1000);

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [enabled, refreshIntervalSeconds, qtRefetch]);

  // ---------------------------------------------------------------------------
  // Manual Refetch
  // ---------------------------------------------------------------------------
  const refetch = useCallback(async () => {
    // Refresh component-level query template data
    await qtRefetch();

    // Also refresh pin data bindings (they register under sub-IDs via PinBindingFetcher)
    if (hasPinData && pins) {
      const pinRefetches: Promise<void>[] = [];
      for (const pin of pins) {
        for (const binding of pin.dataBindings ?? []) {
          const fn = getRefetch(`${componentId}-pin-${binding.id}`);
          if (fn) pinRefetches.push(fn());
        }
      }
      await Promise.all(pinRefetches);
    }

    setLastUpdated(new Date());
  }, [qtRefetch, componentId, hasPinData, pins]);

  // ---------------------------------------------------------------------------
  // Refresh Store Integration (badge timestamp + refetch registry)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    registerRefetch(componentId, refetch);
    return () => unregisterRefetch(componentId);
  }, [componentId, refetch]);

  useEffect(() => {
    if (lastUpdated) {
      useRefreshStore.getState().registerTimestamp(componentId, lastUpdated);
    } else if (hasPinData) {
      // Pins have data bindings but no fetch has happened yet — register
      // an initial timestamp so the badge doesn't show "Awaiting data".
      useRefreshStore.getState().registerTimestamp(componentId, new Date());
    }
    return () => useRefreshStore.getState().unregisterTimestamp(componentId);
  }, [componentId, lastUpdated, hasPinData]);

  // ---------------------------------------------------------------------------
  // Toggle Real-Time Mode
  // ---------------------------------------------------------------------------
  const toggleRealTime = useCallback((enabled: boolean) => {
    setIsRealTimeEnabled(enabled);
  }, []);

  // ---------------------------------------------------------------------------
  // Update Refresh Interval
  // ---------------------------------------------------------------------------
  const setRefreshInterval = useCallback((seconds: number) => {
    setRefreshIntervalSeconds(Math.max(0, seconds));
  }, []);

  // ---------------------------------------------------------------------------
  // Cleanup on Unmount
  // ---------------------------------------------------------------------------
  useEffect(() => {
    mountedRef.current = true;
    const updateTimeouts = updateTimeoutsRef.current;

    return () => {
      mountedRef.current = false;

      // Clear all update timeouts
      updateTimeouts.forEach((timeout) => clearTimeout(timeout));
      updateTimeouts.clear();

      // Clear debounce timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Clear refresh interval
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }

      // Clear state check interval
      if (stateCheckIntervalRef.current) {
        clearInterval(stateCheckIntervalRef.current);
      }
    };
  }, []);

  // ---------------------------------------------------------------------------
  // Memoized Recently Updated Set
  // ---------------------------------------------------------------------------
  const recentlyUpdated = useMemo(() => {
    return new Set(recentlyUpdatedIds);
  }, [recentlyUpdatedIds]);

  // ---------------------------------------------------------------------------
  // Return Hook Result
  // ---------------------------------------------------------------------------
  return {
    data: sensorData,
    isLoading: qtLoading,
    error: qtError,
    refetch,
    lastUpdated,
    connectionStatus,
    recentlyUpdated,
    sensorStates,
    isRealTimeEnabled,
    toggleRealTime,
    setRefreshInterval,
  };
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Calculate time since last update for a sensor
 * @param lastSeen Last seen timestamp
 * @returns Time in milliseconds since last update
 */
export function getTimeSinceUpdate(lastSeen: Date): number {
  return Date.now() - lastSeen.getTime();
}

/**
 * Format time since last update for display
 * @param lastSeen Last seen timestamp
 * @returns Human-readable time string
 */
export function formatTimeSinceUpdate(lastSeen: Date): string {
  const seconds = Math.floor(getTimeSinceUpdate(lastSeen) / 1000);

  if (seconds < 60) {
    return `${seconds}s ago`;
  } else if (seconds < 3600) {
    return `${Math.floor(seconds / 60)}m ago`;
  } else {
    return `${Math.floor(seconds / 3600)}h ago`;
  }
}

export default useMapSensorData;
